"use client";

import React from "react";
import { Facebook, Instagram, Twitter, ArrowRight, Loader, MapPin, Phone, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
function FooterNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 bg-primary-foreground/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-primary-foreground/80 hover:text-secondary transition-colors duration-300 text-sm">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-2 border-b border-secondary/50">
        <p className="text-secondary text-sm font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="relative flex items-center">
                <input type="email" placeholder="Enter your email address" className="w-full bg-transparent border-b border-primary-foreground/30 py-2 pr-10 text-sm text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none focus:border-secondary transition-colors rounded-none" {...field} />
                <Button type="submit" size="icon" variant="ghost" className="absolute right-0 hover:bg-transparent text-primary-foreground hover:text-secondary" disabled={isSubmitting}>
                  {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                  <span className="sr-only">Subscribe</span>
                </Button>
              </div>
            </FormControl>
            <FormMessage className="text-secondary/80 text-xs mt-1" />
            {submitError && <p className="text-secondary/80 text-xs mt-1">{submitError}</p>}
          </FormItem>} />
    </Form>;
}
export default function Footer() {
  const socialLinks = [{
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }];
  return <section id="footer" className="bg-primary text-primary-foreground pt-20 pb-10 border-t border-primary-foreground/10">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600482/img/vineessence_logo_light.png" alt="VineEssence" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed max-w-xs">
              Uncork the essence of your winery with a sophisticated website template designed to highlight your vineyard.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} className="text-primary-foreground/60 hover:text-secondary transition-colors duration-300" aria-label={social.label}>
                    <Icon className="size-5" />
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div>
            <h3 className="font-serif text-xl text-secondary mb-6">Explore</h3>
            <MenuProvider menu_id="23">
              <FooterNav />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="font-serif text-xl text-secondary mb-6">Cellar Notes</h3>
            <p className="text-primary-foreground/70 text-sm mb-4">
              Subscribe to receive updates on new releases, exclusive events, and vineyard news.
            </p>
            <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Welcome to the Cellar Club. Your subscription is confirmed.">
              <NewsletterForm />
            </FormProvider>
          </div>

          {}
          <div>
            <h3 className="font-serif text-xl text-secondary mb-6">Visit Us</h3>
            <ul className="space-y-4 text-sm text-primary-foreground/80">
              <li className="flex items-start gap-3">
                <MapPin className="size-4 text-secondary shrink-0 mt-0.5" />
                <span>Napa Valley, California, USA</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="size-4 text-secondary shrink-0" />
                <span>(707) 555-1234</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="size-4 text-secondary shrink-0" />
                <span>contact@vineessence.com</span>
              </li>
            </ul>
          </div>

        </div>

        {}
        <div className="pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-primary-foreground/50">
          <p>&copy; {new Date().getFullYear()} VineEssence. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-secondary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-secondary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </section>;
}